import { getHttpClient } from './httpClient';

export interface UserListItem {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  roles: string[];
  status: 'pending' | 'active' | 'suspended' | 'locked';
  is_active: boolean;
  deleted_at: string | null;
  last_login_at: string | null;
  enterprise_id?: string | null;
  enterprise_name?: string | null;
}

export interface CreateUserData {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  role?: string;
  enterprise_id?: string;
  team_id?: string;
  /**
   * Multi-plan (PR3) : requis si l'enterprise a plus d'un plan acheté
   * (subscription_plan_seats > 1 ligne). Backend rejette avec 400 sinon.
   */
  plan_tier_id?: string;
}

export interface PlanSeatAvailability {
  planTierId: string;
  planSlug: string;
  planName: string;
  purchased: number;
  used: number;
  remaining: number;
}

export async function getEnterprisePlanSeats(enterpriseId: string): Promise<PlanSeatAvailability[]> {
  const response = await getHttpClient().get<{ plans: PlanSeatAvailability[] }>(
    `/api/admin/enterprises/${enterpriseId}/plan-seats-availability`,
  );
  return response.data.plans;
}

export interface CreateUserResponse {
  ok: boolean;
  user: {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
  };
}

export interface UpdateUserData {
  first_name?: string;
  last_name?: string;
  email?: string;
  role?: string;
  enterprise_id?: string | null;
}

export async function listUsers(includeDeleted = false): Promise<UserListItem[]> {
  const response = await getHttpClient().get<{ users: UserListItem[] }>(
    `/api/admin/users?includeDeleted=${includeDeleted}`
  );
  return response.data.users || [];
}

export async function createUser(data: CreateUserData): Promise<CreateUserResponse> {
  const response = await getHttpClient().post<CreateUserResponse>('/api/admin/users', data);
  return response.data;
}

export async function updateUser(userId: string, data: UpdateUserData): Promise<void> {
  await getHttpClient().put(`/api/admin/users/${userId}`, data);
}

export async function deleteUser(userId: string): Promise<void> {
  await getHttpClient().delete(`/api/admin/users/${userId}`);
}

export type FootprintCategory =
  | 'problems'
  | 'macroplans'
  | 'macroplan_notes'
  | 'support_conversations'
  | 'hansei_sessions'
  | 'hansei_items'
  | 'hansei_votes'
  | 'roles_granted'
  | 'team_app_grants'
  | 'team_chart_grants';

export interface FootprintEntry {
  category: FootprintCategory;
  count: number;
}

export type PermanentDeleteBlockReason = 'has_data' | 'user_still_active';

export interface PermanentDeleteBlockedDetails {
  reason: PermanentDeleteBlockReason;
  footprint?: FootprintEntry[];
}

export class PermanentDeleteBlockedError extends Error {
  constructor(
    message: string,
    public readonly details: PermanentDeleteBlockedDetails,
  ) {
    super(message);
    this.name = 'PermanentDeleteBlockedError';
  }
}

export async function permanentlyDeleteUser(userId: string): Promise<void> {
  try {
    await getHttpClient().delete(`/api/admin/users/${userId}/permanent`);
  } catch (err: unknown) {
    const axiosErr = err as { response?: { status?: number; data?: { error?: string; details?: PermanentDeleteBlockedDetails } } };
    if (axiosErr.response?.status === 409 && axiosErr.response.data?.details) {
      throw new PermanentDeleteBlockedError(
        axiosErr.response.data.error ?? 'Permanent deletion blocked',
        axiosErr.response.data.details,
      );
    }
    throw err;
  }
}

export async function reactivateUser(userId: string): Promise<void> {
  await getHttpClient().post(`/api/admin/users/${userId}/reactivate`);
}

export async function unlockUser(userId: string): Promise<void> {
  await getHttpClient().post(`/api/admin/users/${userId}/unlock`);
}

export async function resetUserPassword(userId: string): Promise<{ temporaryPassword: string }> {
  const response = await getHttpClient().post<{ ok: boolean; temporaryPassword: string }>(
    `/api/admin/users/${userId}/reset-password`
  );
  return { temporaryPassword: response.data.temporaryPassword };
}
